<template>
  <div class="timeline__wrapper">
    <div
      v-for="item in events"
      :key="item.id">
      <div class="timeline__item">
        <!-- Icon: incoming_call uses img, others use font-awesome-icon -->
        <span
          v-if="item.type === 'incoming_call'"
          class="timeline__icon">
          <img
            style="width:14px;margin-top: -2px"
            :src="getIcon(item)">
        </span>
        <span
          v-else-if="getIcon(item)"
          :class="['timeline__icon', getIconColor(item)]">
          <font-awesome-icon :icon="getIcon(item)" />
        </span>

        <!-- Head section -->
        <div class="timeline__head">
          <p
            v-if="item.created_at"
            class="timeline__date">
            {{ item.created_at | dateFilter }}
          </p>
          <!-- User name rendering -->
          <p
            v-if="item.type === 'closing'"
            class="timeline__name">
            <font-awesome-icon :icon="dataIconUser" />
            &nbsp;{{ item.closed_by_name || '' }}
          </p>
          <p
            v-else-if="item.type === 'set_operator' && !isEmpty(item.user_name)"
            class="timeline__name">
            <font-awesome-icon :icon="dataIconUser" />
            &nbsp;{{ item.user_name }}
          </p>
          <p
            v-else-if="item.type === 'sms'"
            :class="['timeline__name', 'sms', getIconColor(item)]">
            &bull; {{ getSmsStatusText(item.status) }}
          </p>
          <p
            v-else-if="item.type === 'issue_comment'"
            class="timeline__name">
            Issue
          </p>
          <p
            v-else-if="!isEmpty(item.operator_name) || !isEmpty(item.user_name)"
            class="timeline__name">
            <font-awesome-icon :icon="dataIconUser" />
            &nbsp;{{ item.operator_name || item.user_name || '' }}
          </p>
        </div>

        <!-- Body section -->
        <div class="timeline__body">
          <!-- Outcoming call -->
          <template v-if="item.type === 'outcoming_call'">
            <p v-if="item.status === 'success'">
              Success Call, {{ item.duration | dateCall }}
            </p>
            <p v-if="item.status === 'success' && item.feedback">
              {{ item.feedback }}
            </p>
            <CallButton
              v-if="item.status === 'success' && !item.issue_name"
              type="warning"
              plain
              size="min"
              :icon="dataIconComment"
              @click="$emit('on-add-issues', item)">
              Add Issue
            </CallButton>
            <p v-else-if="item.status === 'cancelled'">
              Cancelled Call
            </p>
            <p v-else-if="item.status === 'noanswer'">
              Call No Answer for {{ item.updated_at | dateFilter('HH:mm') }}
            </p>
            <p v-else-if="item.status === 'rejected'">
              Call Rejected
            </p>
            <p v-else-if="item.status === 'invalid_number'">
              Call Invalid Number
            </p>
            <p v-else-if="item.status === 'not_available'">
              Not Available
            </p>
          </template>

          <!-- Incoming call -->
          <template v-else-if="item.type === 'incoming_call'">
            <p v-if="item.status === 'success'">
              Incoming Call, {{ item.duration | dateCall }}
            </p>
            <p v-if="item.status === 'success' && item.feedback">
              {{ item.feedback }}
            </p>
          </template>

          <!-- Comment type -->
          <p v-else-if="item.type === 'comment'">
            {{ item.comment || '' }}
          </p>

          <!-- set_execute_before -->
          <p v-else-if="item.type === 'set_execute_before'">
            Scheduled for {{ item.scheduled_at | dateFilter }}
          </p>

          <!-- set_operator -->
          <p v-else-if="item.type === 'set_operator'">
            Assigned to <strong>{{ item.operator_name }}</strong>
          </p>

          <!-- sms -->
          <p v-else-if="item.type === 'sms'">
            <strong>SMS:</strong> {{ item.template_name }}
          </p>

          <!-- set_bonus -->
          <p v-else-if="item.type === 'set_bonus'">
            <strong>Bonus Given:</strong> {{ item.description }}
          </p>

          <!-- closing -->
          <p v-else-if="item.type === 'closing'">
            <strong>Closed task</strong><br>{{ item.comment }}
          </p>

          <!-- issue_comment -->
          <p v-else-if="item.type === 'issue_comment'">
            <strong>{{ item.issue_name }}</strong>
            <br>{{ item.comment }}
          </p>

          <!-- Edit / Delete buttons -->
          <template
            v-if="(computedIsAdmin || computedCurrentUser.id === item.user_id) &&
              (item.type === 'comment' || item.type === 'issue_comment') &&
              isDisabledEditComment(item)">
            <CallButton
              v-if="computedCurrentUser.id === item.user_id || computedIsAdmin"
              outlet
              not-border
              size="mini"
              type="success"
              :icon="dataIconPen"
              @click="handleEditComment(item)">
              Edit
            </CallButton>
            <CallButton
              v-if="computedIsAdmin"
              outlet
              not-border
              size="mini"
              type="danger"
              :icon="dataIconTrash"
              @click="handleDeleteComment(item)">
              Delete
            </CallButton>
          </template>
        </div>
      </div>
    </div>
  </div>
</template>

<script>
import {
  faCheck,
  faPen,
  faTrashAlt,
  faUser
} from '@fortawesome/pro-regular-svg-icons';
import { faGift, faClock, faPhone, faComment, faSms } from '@fortawesome/pro-solid-svg-icons';
import CallButton from './CallButton';
import { mapGetters } from 'vuex';
import helper from '../../service/helper';

export default {
  name: 'CallTimeLine',
  components: { CallButton },
  props: {
    events: {
      type: Array,
      default: () => []
    }
  },
  data () {
    return {
      dataIconClock: faClock,
      dataIconPhone: faPhone,
      dataIconComment: faComment,
      dataIconCheck: faCheck,
      dataIconUser: faUser,
      dataIconPen: faPen,
      dataIconTrash: faTrashAlt,
      dataIconSms: faSms,
      dataIconGift: faGift
    };
  },
  computed: {
    ...mapGetters({
      computedIsAdmin: 'auth/isAdmin',
      computedIsOperator: 'auth/isOperator',
      computedCurrentUser: 'auth/currentUser'
    })
  },
  methods: {
    isEmpty (val) {
      return helper.isEmpty(val);
    },

    getIcon (item) {
      const { type, status } = item;
      switch (type) {
        case 'outcoming_call':
          return this.dataIconPhone;
        case 'issue_comment':
          return this.dataIconComment;
        case 'comment':
          return this.dataIconComment;
        case 'closing':
          return this.dataIconCheck;
        case 'set_execute_before':
          return this.dataIconClock;
        case 'set_operator':
          return this.dataIconUser;
        case 'sms':
          return this.dataIconSms;
        case 'set_bonus':
          return this.dataIconGift;
        case 'incoming_call':
          return '/img/incoming-outline.svg';
        default:
          return null;
      }
    },

    getIconColor (item) {
      const { type, status } = item;
      switch (type) {
        case 'outcoming_call':
          if (status === 'success') return 'success';
          if (['not_available', 'invalid_number', 'cancelled'].includes(status)) return 'danger';
          if (status === 'noanswer') return 'warning';
          if (status === 'rejected') return 'warning';
          return '';
        case 'incoming_call':
        case 'comment':
        case 'closing':
        case 'set_operator':
        case 'set_bonus':
        case 'issue_comment':
          return 'success';
        case 'set_execute_before':
          return 'warning';
        case 'sms':
          if (status === 'success') return 'success';
          if (status === 'error') return 'danger';
          if (status === 'new') return 'warning';
          return '';
        default:
          return '';
      }
    },

    getSmsStatusText (status) {
      if (status === 'new') return 'Sent';
      if (status === 'success') return 'Delivered';
      return "Wasn't Sent";
    },

    isDisabledEditComment (item) {
      if (item.type === 'issue_comment') {
        return true;
      }
      const created = new Date(item.created_at).getTime();
      const tzOffset = (-(new Date().getTimezoneOffset() / 60)).toFixed(0);
      return created + (3600000 * tzOffset) + 600000 > Date.now();
    },

    handleEditComment (item) {
      this.$emit('on-comment', {
        ...(item.type === 'comment' && {
          id: item.id,
          comment: item.comment
        }) || {},
        ...(item.type === 'issue_comment' && item) || {},
        type: 'edit'
      });
    },

    handleDeleteComment (item) {
      this.$emit('on-comment', {
        ...(item.type === 'comment' && {
          id: item.id
        }) || {},
        ...(item.type === 'issue_comment' && item) || {},
        type: 'delete'
      });
    }
  }
};
</script>
